import React, { useEffect, useState } from 'react'

import { CardHeader } from '@mui/material'
import subDays from 'date-fns/subDays'
import CustomerAuditFilters from 'features/customers/components/audit/CustomerAuditFilters'
import { useAuditEvents } from 'features/customers/hooks/useAuditEvents'
import debounce from 'lodash/debounce'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'

import Audit from 'components/audit/Audit'
import AuditEmailDetailsView from 'components/audit/AuditEmailDetailsView'
import AuditSignalsDetailsView from 'components/audit/AuditSignalsDetailsView'
import AuditWorkflowDetailsView from 'components/audit/AuditWorkflowDetailsView'

import {
  EMAIL_CONFIG,
  PROCESSED_SIGNALS_CONFIG,
  PROCESSED_WORKFLOWS_CONFIG,
  UNPROCESSED_SIGNALS_CONFIG,
  UNPROCESSED_WORKFLOWS_CONFIG,
} from './configs'

const transformWorkflowsData = (data: any[]) =>
  data.map(({ timestamp, payload, meta }) => ({
    timestamp,
    workflow: payload?.workflow,
    lead: payload?.lead,
    leadType: payload?.leadType,
    error: meta?.error,
    source: meta?.source,
  }))

const transformSignalsData = (data: any[]) =>
  data.map(({ timestamp, payload, meta }) => ({
    timestamp,
    workflow: payload?.workflow,
    signals: payload?.signals,
    leadType: 'signals',
    error: meta?.error,
    source: meta?.source,
  }))

const CustomerAudit = () => {
  // Form state is lifted here so this parent can read filter values; CustomerAuditFilters renders the fields via the shared FormProvider.
  const [searchParams] = useSearchParams()
  const linkedFrom = searchParams.get('date_from')
  const linkedWorkflowId = searchParams.get('workflow_id')
  const methods = useForm({
    defaultValues: {
      date_from: linkedFrom ? new Date(linkedFrom) : subDays(new Date(), 7),
      date_to: new Date(),
      workflow_id: linkedWorkflowId ? Number(linkedWorkflowId) : null,
      uuid: searchParams.get('uuid'),
    },
  })
  const [debouncedUuid, setDebouncedUuid] = useState(searchParams.get('uuid') ?? '')
  const { date_from, date_to, workflow_id, uuid } = useWatch({ control: methods.control })

  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedUuid(uuid ?? '')
    }, 500)
    handler()
    return () => {
      handler.cancel()
    }
  }, [uuid])

  const data = {
    date_from,
    date_to,
    workflow_id: workflow_id || undefined,
    uuid: debouncedUuid || undefined,
  }

  const workflowProcessedEvents = useAuditEvents('topProcessedLeads', data, transformWorkflowsData)
  const workflowUnProcessedEvents = useAuditEvents('topUnprocessedLeads', data, transformWorkflowsData)
  const customerEmailEvents = useAuditEvents('mailSend', data)
  const signalsProcessedEvents = useAuditEvents('topProcessedSignals', data, transformSignalsData)
  const signalsUnprocessedEvents = useAuditEvents('topUnprocessedSignals', data, transformSignalsData)

  const config = [
    {
      type: 'topProcessedLeads',
      label: 'Workflows Processed',
      config: PROCESSED_WORKFLOWS_CONFIG,
      isLoading: workflowProcessedEvents.isLoading,
      data: workflowProcessedEvents.data,
      detailView: AuditWorkflowDetailsView,
    },
    {
      type: 'topUnprocessedLeads',
      label: 'Workflows Unprocessed',
      config: UNPROCESSED_WORKFLOWS_CONFIG,
      isLoading: workflowUnProcessedEvents.isLoading,
      data: workflowUnProcessedEvents.data,
      detailView: AuditWorkflowDetailsView,
    },
    {
      type: 'mailSend',
      label: 'Sent Emails',
      config: EMAIL_CONFIG,
      isLoading: customerEmailEvents.isLoading,
      data: customerEmailEvents.data,
      detailView: AuditEmailDetailsView,
    },
    {
      type: 'topProcessedSignals',
      label: 'Bombora Signals Processed',
      config: PROCESSED_SIGNALS_CONFIG,
      isLoading: signalsProcessedEvents.isLoading,
      data: signalsProcessedEvents.data,
      detailView: AuditSignalsDetailsView,
    },
    {
      type: 'topUnprocessedSignals',
      label: 'Bombora Signals Unprocessed',
      config: UNPROCESSED_SIGNALS_CONFIG,
      isLoading: signalsUnprocessedEvents.isLoading,
      data: signalsUnprocessedEvents.data,
      detailView: AuditSignalsDetailsView,
    },
  ]

  return (
    <FormProvider {...methods}>
      <CardHeader action={<CustomerAuditFilters />} title="Audit" />
      <Audit config={config} />
    </FormProvider>
  )
}

export default CustomerAudit
